import type { FieldValues } from 'react-hook-form';
import { type AxiosResponse } from 'axios';

import { request } from '../../../../services/requestService';

import {
  type IAdditionalFieldDbOptionResponse,
  type IAdditionalFieldOptionsResponse,
  type IFeesToBePaidResponse,
  type IGeneratedItemUsageHistoryResponse,
  type IItemResponse,
  type IItemUsageHistoryResponse,
  type ILpcoFormDetails,
} from '../../types/response';
import { Operators, type SearchResult } from '../../../../@types/serviceTypes';
import { SortDirection } from '../../../../constants';
import { RequestMethods } from '../../types/enums';

import { LPCO_ADMIN_SERVER } from '../../../../config';

const ENDPOINTS = {
  lpco: 'lpco',
  formDetails: 'formDetails',
  userRefNumber: 'userRefNumber',
  report: 'report',
  print: 'print',
  preview: 'preview',
  additionalFieldDbOption: 'additionalFieldDbOption',
  search: 'search',
  itemUsageHistory: 'itemUsageHistory',
  generationItemHistory: 'generationItemHistory',
  feesToBePaid: 'fees/toBePaid',
  validateItem: 'item/validateItem',
};

interface IOperationParams {
  operation: string;
  data: FieldValues;
  id?: string;
}

interface IAdditionalFieldOptionsParams {
  optionTypeId: number;
  searchText: string;
  signal?: AbortSignal;
}

interface IItemUsageHistoryParams {
  lpcoId: string;
  itemNumber: number;
}
interface IGeneratedItemUsageHistoryParams {
  itemId: number;
}

export function getLpcoFormDetails(id: number): Promise<AxiosResponse<ILpcoFormDetails>> {
  return request({
    url: `${ENDPOINTS.lpco}/${ENDPOINTS.formDetails}/${id}`,
  });
}

export function getUserReferenceNumber(): Promise<AxiosResponse<string>> {
  return request({
    url: `${ENDPOINTS.lpco}/${ENDPOINTS.userRefNumber}`,
  });
}

export function lpcoOperationRequest({ data, operation, id }: IOperationParams): Promise<AxiosResponse> {
  let method = RequestMethods.PUT;
  let url = `${ENDPOINTS.lpco}/${operation}/${id as string}`;

  if (!id) {
    method = RequestMethods.POST;
    url = `${ENDPOINTS.lpco}/${operation}`;
  }

  return request({
    method,
    url,
    data,
  });
}

export function downloadLpcoData(id: string, isPreview = false): Promise<AxiosResponse<Blob>> {
  return request({
    url: `${ENDPOINTS.report}/${isPreview ? ENDPOINTS.preview : ENDPOINTS.print}/${id}`,
    responseType: 'blob',
  });
}

export function getAdditionalFieldOptions({
  optionTypeId,
  searchText,
  signal,
}: IAdditionalFieldOptionsParams): Promise<AxiosResponse<IAdditionalFieldOptionsResponse>> {
  const url = searchText
    ? `${ENDPOINTS.additionalFieldDbOption}/${ENDPOINTS.search}/${optionTypeId}/${searchText}`
    : `${ENDPOINTS.additionalFieldDbOption}/${ENDPOINTS.search}/${optionTypeId}`;

  return request({
    baseURL: LPCO_ADMIN_SERVER,
    url,
    signal,
  });
}

export function getItemUsageHistory({
  lpcoId,
  itemNumber,
}: IItemUsageHistoryParams): Promise<AxiosResponse<IItemUsageHistoryResponse[]>> {
  return request({
    url: `${ENDPOINTS.itemUsageHistory}/${lpcoId}/${itemNumber}`,
  });
}

export function getGeneratedItemUsageHistory({
  itemId,
}: IGeneratedItemUsageHistoryParams): Promise<AxiosResponse<IGeneratedItemUsageHistoryResponse[]>> {
  return request({
    url: `${ENDPOINTS.generationItemHistory}/${itemId}`,
  });
}

export function getFeesToBePaid(lpcoId: string): Promise<AxiosResponse<IFeesToBePaidResponse>> {
  return request({
    url: `${ENDPOINTS.feesToBePaid}/${lpcoId}`,
  });
}

export function getDbOptionById({
  id,
  typeId,
}: {
  id: string;
  typeId: number;
}): Promise<AxiosResponse<SearchResult<IAdditionalFieldDbOptionResponse>>> {
  return request({
    baseURL: LPCO_ADMIN_SERVER,
    method: 'POST',
    url: `${ENDPOINTS.additionalFieldDbOption}/${ENDPOINTS.search}`,
    data: {
      offset: 0,
      restrictBy: 'AND',
      sortByFields: {
        id: SortDirection.DESC,
      },
      selectFields: ['id', 'code', 'name', 'name2', 'typeId'],
      restrictions: [
        {
          value: id,
          field: 'id',
          operator: Operators.EQUALS,
        },
        {
          value: typeId,
          field: 'typeId',
          operator: Operators.EQUALS,
        },
      ],
    },
  });
}

export function validateItem({
  licenseTypeId,
  itemIndex,
  data,
}: {
  licenseTypeId: number;
  itemIndex: number;
  data: IItemResponse;
}): Promise<AxiosResponse> {
  return request({
    method: 'POST',
    url: `${ENDPOINTS.validateItem}/${licenseTypeId}/${itemIndex}`,
    data,
  });
}
